import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  CalendarCheck,
  CalendarDays,
  Calendar,
  Wallet,
  FolderOpen,
  Sparkles,
  Target,
  Package,
  Receipt,
  GraduationCap,
  FileText,
  Settings,
  Cable,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Brain,
  Briefcase,
  UserPlus,
  UserMinus,
  Gift,
  FlaskConical,
  ChevronDown,
  Building2,
  Stamp,
  Plane,
  Shield,
  User,
  Network,
  IndianRupee,
  ShieldCheck,
  HeartHandshake,
  PartyPopper,
  Zap,
  Heart,
  Headphones,
  Banknote,
  Flame,
  TrendingUp,
  Gamepad2,
  Mail,
  Inbox,
  Clock,
  Award,
  Quote,
} from 'lucide-react';

const LOGO_URL = "/favicon.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', fixed: 'top' },
  { label: 'Access Control', icon: Shield, path: '/rbac', roles: ['admin'] },
  { label: 'Integrations', icon: Cable, path: '/admin/integrations', roles: ['admin', 'hr'] },
  { label: 'Security', icon: Shield, path: '/admin/security', roles: ['admin'] },
  { 
    label: 'AI & Skills', 
    icon: Brain, 
    children: [
      { label: 'AI Assistant', icon: Brain, path: '/ai-assistant' },
      { label: 'Contributions', icon: Target, path: '/contributions' },
      { label: 'Skills Matrix', icon: Sparkles, path: '/skills' },
    ]
  },
  { label: 'Assets', icon: Package, path: '/assets' },
  { label: 'Benefits', icon: Gift, path: '/benefits' },
  { label: 'Billing', icon: IndianRupee, path: '/billing', roles: ['admin', 'hr'] },
  { label: 'Compliance', icon: ShieldCheck, path: '/compliance' },
  { label: 'Policies', icon: FileText, path: '/policies' },
  { label: 'Engage', icon: Heart, path: '/engage' },
  { label: 'Programs', icon: PartyPopper, path: '/programs' },
  { label: 'Expenses', icon: Receipt, path: '/expenses' },
  { label: 'Gopal', icon: HeartHandshake, path: '/gopal' },
  { label: 'Helpdesk', icon: Headphones, path: '/helpdesk' },
  { label: 'Insights', icon: Mail, path: '/insights', roles: ['admin', 'hr'] },
  { label: 'Learning', icon: GraduationCap, path: '/learning' },
  { label: 'Letters', icon: Stamp, path: '/letters' },
  { label: 'Loans', icon: Banknote, path: '/loans' },
  { label: 'OKRs & Goals', icon: Target, path: '/okrs' },
  { label: 'Performance', icon: TrendingUp, path: '/performance' },
  { 
    label: 'Payroll', 
    icon: Wallet, 
    roles: ['admin', 'hr'],
    children: [
      { label: 'Analytics', icon: FileText, path: '/payroll-dashboard' },
      { label: 'Payslips', icon: Wallet, path: '/payroll' },
    ]
  },
  { 
    label: 'People', 
    icon: Users, 
    children: [
      { label: 'ATS', icon: Briefcase, path: '/ats' },
      { label: 'Employees', icon: Users, path: '/employees' },
      { label: 'Internships', icon: GraduationCap, path: '/internships' },
      { label: 'Onboarding', icon: UserPlus, path: '/onboarding' },
      { label: 'Onboarding & Compliance', icon: ShieldCheck, path: '/onboarding-compliance' },
      { label: 'Exit Management', icon: UserMinus, path: '/exit-management' },
    ]
  },
  { label: 'Reports', icon: FileText, path: '/reports', roles: ['admin', 'hr', 'manager'] },
  { label: 'Approvals', icon: Inbox, path: '/approvals', roles: ['admin', 'hr', 'manager'] },
  { label: 'Research', icon: FlaskConical, path: '/research' },
  { label: 'Team Kernel', icon: Network, path: '/team-kernel' },
  { 
    label: 'Time & Leave', 
    icon: CalendarCheck, 
    children: [
      { label: 'Attendance', icon: CalendarCheck, path: '/attendance' },
      { label: 'Holidays', icon: Calendar, path: '/holidays' },
      { label: 'Leaves', icon: CalendarDays, path: '/leaves' },
    ]
  },
  { label: 'Travel', icon: Plane, path: '/travel' },
  { label: 'Wishes', icon: PartyPopper, path: '/wishes', roles: ['admin', 'hr'] },
  { label: 'Thought Library', icon: Quote, path: '/thought-library', roles: ['admin', 'hr'] },
  { label: 'Witties', icon: Flame, path: '/witties' },
  { label: 'Wordle', icon: Gamepad2, path: '/wordle' },
  // Personal items at the bottom
  { label: 'My Payslips', icon: Wallet, path: '/my-payslips', fixed: 'bottom' },
  { label: 'My Profile', icon: User, path: '/profile', fixed: 'bottom' },
  { label: 'Settings', icon: Settings, path: '/settings', fixed: 'bottom' },
];

// ===== Employee-only grouped navigation (matches the approved wireframe) =====
const employeeNavGroups = [
  {
    label: 'Me', icon: User, children: [
      { label: 'My Profile', icon: User, path: '/profile' },
      { label: 'My Employment Details', icon: Briefcase, path: '/profile' },
      { label: 'My Family & Dependents', icon: Users, path: '/profile' },
      { label: 'My Compliance Completion', icon: ShieldCheck, path: '/compliance' },
      { label: 'My Asset Details', icon: Package, path: '/assets' },
      { label: 'My Onboarding', icon: UserPlus, path: '/my-onboarding' },
    ],
  },
  {
    label: 'Pay & Benefits', icon: Wallet, children: [
      { label: 'My Salary Structure', icon: Wallet, path: '/my-payslips' },
      { label: 'My Letters', icon: Stamp, path: '/letters' },
      { label: 'Tax & Declarations', icon: IndianRupee, path: '/my-payslips' },
      { label: 'Benefits & Insurance', icon: Gift, path: '/benefits' },
      { label: 'Loans', icon: Banknote, path: '/loans' },
    ],
  },
  {
    label: 'Time & Attendance', icon: CalendarCheck, children: [
      { label: 'Mark Attendance / Regularization', icon: CalendarCheck, path: '/attendance' },
      { label: 'Leave', icon: CalendarDays, path: '/leaves' },
      { label: 'Holidays', icon: Calendar, path: '/holidays' },
    ],
  },
  {
    label: 'Goals & Performance', icon: Target, children: [
      { label: 'My Goals', icon: Target, path: '/okrs' },
      { label: 'Performance', icon: TrendingUp, path: '/performance' },
      { label: 'Feedback', icon: Sparkles, path: '/performance' },
      { label: '1:1s', icon: Users, path: '/performance' },
      { label: 'Reviews', icon: FileText, path: '/performance' },
    ],
  },
  {
    label: 'Growth & Learning', icon: GraduationCap, children: [
      { label: 'Learning & Development', icon: GraduationCap, path: '/learning' },
      { label: 'Certifications', icon: Sparkles, path: '/skills' },
    ],
  },
  {
    label: 'Travel & Expenses', icon: Plane, children: [
      { label: 'Travel Requests', icon: Plane, path: '/travel' },
      { label: 'My Trips', icon: Plane, path: '/travel' },
      { label: 'Expenses Claims', icon: Receipt, path: '/expenses' },
      { label: 'Reimbursements', icon: IndianRupee, path: '/expenses' },
    ],
  },
  {
    label: 'My Workplace', icon: Building2, children: [
      { label: 'My Team', icon: Network, path: '/team-kernel' },
    ],
  },
  {
    label: 'Engagement', icon: Heart, children: [
      { label: 'Recognition & Rewards', icon: Award, path: '/engage' },
      { label: 'Events & Programs', icon: PartyPopper, path: '/programs' },
      { label: 'Wordle', icon: Gamepad2, path: '/wordle' },
      { label: 'Witties Leaderboard', icon: Flame, path: '/witties' },
      { label: 'Merchandise', icon: Gift, path: '/engage' },
    ],
  },
  {
    label: 'My HR', icon: Headphones, children: [
      { label: 'KOSH / AI Assistant', icon: Brain, path: '/ai-assistant' },
      { label: 'Company Policies', icon: FileText, path: '/policies' },
      { label: 'Policies & FAQs', icon: FileText, path: '/helpdesk' },
      { label: 'Separation', icon: UserMinus, path: '/exit-management' },
      { label: 'Refer for Open Position', icon: UserPlus, path: '/ats' },
      { label: 'Internal Opportunities', icon: Briefcase, path: '/ats' },
      { label: 'My Tickets', icon: Headphones, path: '/helpdesk' },
    ],
  },
  {
    label: 'My Tasks', icon: Inbox, children: [
      { label: 'Approvals', icon: Inbox, path: '/approvals' },
      { label: 'Pending Actions', icon: Clock, path: '/approvals' },
      { label: 'Notifications', icon: Mail, path: '/approvals' },
    ],
  },
];

const NavItem = ({ item, isCollapsed, closeMobile, permissions, isPinned, onTogglePin, showPin, badges }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const badgeCount = badges?.[item.path];
  
  // Check role-based access (hardcoded roles take priority for admin-only items)
  if (item.roles && !item.roles.includes(user?.role)) {
    return null;
  }

  // Check RBAC permissions
  const getPermKey = (path) => path?.replace('/', '').replace(/-/g, '-');
  
  if (item.path && permissions) {
    const permKey = getPermKey(item.path);
    if (permKey && permissions[permKey] === false) return null;
  }

  if (item.children) {
    // Filter children by permissions
    const visibleChildren = item.children.filter(child => {
      const permKey = getPermKey(child.path);
      return !permKey || permissions?.[permKey] !== false;
    });
    if (visibleChildren.length === 0) return null;

    return (
      <div className="mb-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full sidebar-nav-item justify-between ${isOpen ? 'bg-muted/50' : ''}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-toggle`}
        >
          <div className="flex items-center gap-3">
            <item.icon className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>{item.label}</span>}
          </div>
          {!isCollapsed && (
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>
        {isOpen && !isCollapsed && (
          <div className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
            {visibleChildren.map((child) => (
              <NavLink
                key={`${item.label}-${child.label}`}
                to={child.path}
                onClick={closeMobile}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <child.icon className="w-4 h-4 shrink-0" />
                <span className="text-sm">{child.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group relative mb-1">
      <NavLink
        to={item.path}
        onClick={closeMobile}
        className={({ isActive }) =>
          `sidebar-nav-item ${isActive ? 'active' : ''}`
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon className="w-5 h-5 shrink-0" />
        {!isCollapsed && <span className="flex-1">{item.label}</span>}
        {!isCollapsed && badgeCount > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white" data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>{badgeCount}</span>
        )}
        {isCollapsed && badgeCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
        )}
      </NavLink>
      {showPin && !isCollapsed && !item.fixed && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(item.label); }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-all ${isPinned ? 'text-amber-500 opacity-100' : 'opacity-0 group-hover:opacity-60 text-muted-foreground hover:text-amber-500'}`}
          title={isPinned ? 'Unpin' : 'Pin to top (max 3)'}
          data-testid={`pin-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Zap className={`w-3.5 h-3.5 ${isPinned ? 'fill-amber-500' : ''}`} />
        </button>
      )}
    </div>
  );
};

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [navBadges, setNavBadges] = useState({});
  const [pinnedItems, setPinnedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wisewit_pinned_nav') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/rbac/my-permissions`, { withCredentials: true });
        setPermissions(res.data);
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
      }
    };
    if (user) fetchPerms();
  }, [user]);

  // Poll pending notifications (pulse surveys awaiting vote + approvals for HR/Admin/Manager)
  useEffect(() => {
    if (!user) return;
    const isApprover = ['admin', 'hr', 'manager'].includes(user.role);
    const fetchBadges = async () => {
      const next = {};
      try {
        const res = await axios.get(`${API_URL}/api/surveys/pending-count`, { withCredentials: true });
        next['/engage'] = res.data?.count || 0;
      } catch (err) { /* silent */ }
      if (isApprover) {
        try {
          const res = await axios.get(`${API_URL}/api/approvals/pending-count`, { withCredentials: true });
          next['/approvals'] = res.data?.total || 0;
        } catch (err) { /* silent */ }
      }
      setNavBadges(prev => ({ ...prev, ...next }));
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000); // refresh every 30s (approvals move fast)
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPhotoUrl = () => {
    const p = user?.profile_picture;
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return `${API_URL}/api/files/${p}`;
  };

  const togglePin = (label) => {
    setPinnedItems(prev => {
      let next;
      if (prev.includes(label)) {
        next = prev.filter(l => l !== label);
      } else {
        if (prev.length >= 3) {
          next = [...prev.slice(1), label]; // Remove oldest pin, add new
        } else {
          next = [...prev, label];
        }
      }
      localStorage.setItem('wisewit_pinned_nav', JSON.stringify(next));
      return next;
    });
  };

  // Split items: Dashboard (top) -> Pinned -> Alphabetical main -> Personal (bottom)
  const dashboardItem = navItems.find(i => i.fixed === 'top');
  const bottomItems = navItems.filter(i => i.fixed === 'bottom');
  const mainItems = navItems.filter(i => !i.fixed);
  const pinnedNavItems = mainItems.filter(i => pinnedItems.includes(i.label));
  const unpinnedNavItems = mainItems.filter(i => !pinnedItems.includes(i.label));

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 h-screen ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <img 
              src={LOGO_URL} 
              alt="Wisewit" 
              className="w-10 h-10 rounded-md object-cover"
            />
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-lg truncate">Wisewit</h1>
              <p className="text-xs text-muted-foreground">HRMS</p>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-muted rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 p-3">
            <nav className="space-y-0.5">
              {/* Dashboard always first */}
              {dashboardItem && (
                <NavItem key={dashboardItem.label} item={dashboardItem} permissions={permissions} closeMobile={() => setSidebarOpen(false)} showPin={false} badges={navBadges} />
              )}

              {user?.role === 'employee' ? (
                /* Employee grouped navigation (approved wireframe) */
                <div className="mt-1 space-y-0.5">
                  {employeeNavGroups.map((group) => (
                    <NavItem key={group.label} item={group} permissions={permissions} closeMobile={() => setSidebarOpen(false)} showPin={false} badges={navBadges} />
                  ))}
                </div>
              ) : (
                <>
                  {/* Pinned section */}
                  {pinnedNavItems.length > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 px-2 pt-3 pb-1">
                        <Zap className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="text-[10px] uppercase tracking-widest text-amber-500/70 font-semibold">Pinned</span>
                      </div>
                      {pinnedNavItems.map((item) => (
                        <NavItem key={`pin-${item.label}`} item={item} permissions={permissions} closeMobile={() => setSidebarOpen(false)} isPinned={true} onTogglePin={togglePin} showPin={true} badges={navBadges} />
                      ))}
                      <div className="border-b border-border/50 my-2" />
                    </>
                  )}

                  {/* Main items (alphabetical) */}
                  {unpinnedNavItems.map((item) => (
                    <NavItem key={item.label} item={item} permissions={permissions} closeMobile={() => setSidebarOpen(false)} isPinned={false} onTogglePin={togglePin} showPin={true} badges={navBadges} />
                  ))}

                  {/* Personal items at bottom */}
                  <div className="border-t border-border/50 mt-3 pt-2">
                    {bottomItems.map((item) => (
                      <NavItem key={item.label} item={item} permissions={permissions} closeMobile={() => setSidebarOpen(false)} showPin={false} badges={navBadges} />
                    ))}
                  </div>
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
              <Avatar className="h-9 w-9">
                <AvatarImage src={getPhotoUrl()} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top navbar */}
        <header className="sticky top-0 z-30 h-14 border-b border-border bg-card/80 backdrop-blur-sm shrink-0">
          <div className="flex items-center justify-between h-full px-4">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 hover:bg-muted rounded-md"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span>Wisewit Solutions Pvt Ltd</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={getPhotoUrl()} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 hidden sm:inline" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
